import { faFloppyDisk, faPen } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  FormControl,
  FormControlLabel,
  Grid,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import useMediaQuery from '@mui/material/useMediaQuery';
import { toLocaleString, useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useUserApi } from '../../api/user';
import { TelInput } from '../../components/forms/telephoneInput';
import MiscContext from '../../context/MiscContext';
import { delay } from '../../utils/miscellaneous';
import { AutoFillAwareTextField } from '../Auth/base';
import { ProfileBase } from './base';
import { faCoinVertical } from '@fortawesome/pro-regular-svg-icons';

const textStyles = {
  fontSize: 14,
  fontWeight: 500,
  width: 'fit-content',
};

/**
 * React functional component representing the Profile Page.
 * @component
 * @returns {JSX.Element} The rendered ProfilePage component.
 */
export const ProfilePage = () => {
  const [ isEditing, setIsEditing ] = useState( true );
  const [ firstName, setFirstName ] = useState( '' );
  const [ lastName, setLastName ] = useState( '' );
  const [ userEmail, setUserEmail ] = useState( '' );
  const [ userPlan, setUserPlan ] = useState( '' );
  const [ phoneVerified, setPhoneVerified ] = useState( true );
  const [ isLastNamePrevFilled, setIsLastNamePrevFilled ] = useState( null );
  const [ isPhoneNumPrevFilled, setIsPhoneNumPrevFilled ] = useState( null );
  const [ phoneNumber, setPhoneNumber ] = useState( '' );
  const [ isPhoneNumberValid, setIsPhoneNumberValid ] = useState( true );
  const [ prevPhoneNumber, setPrevPhoneNumber ] = useState( '' );
  const [ emailSubscribed, setEmailSubscribed ] = useState( null );
  const [ validationConflict, setValidationConflict ] = useState( false );
  const [ alertMessage, setAlertMessage ] = useState( {
    message: '',
    severity: 'error',
  } );

  const isSmallScreen = useMediaQuery( theme => theme.breakpoints.down( 'sm' ) );
  const isGalaxyFold = useMediaQuery( '(max-width:280px)' );
  const isGalaxyS8Plus = useMediaQuery( '(max-width:360px)' );

  const navigate = useNavigate();
  const userApi = useUserApi();
  const { getUserDataIntoMiscContext,userCredits } = useContext( MiscContext );

  useEffect( () => {
    getUserPrevPhoneNum();
    getUserProfileData();
  }, [] );

  useEffect( () => {
    // Check if severity is "success" and set a timeout to hide the alert after 5 seconds
    if ( alertMessage.severity === 'success' ) {
      const timeoutId = setTimeout( () => {
        setAlertMessage( {
          message: '',
          severity: '',
        } );
      }, 5000 );

      // Clear the timeout when the component unmounts or when the severity changes
      return () => clearTimeout( timeoutId );
    }
  }, [ alertMessage.severity ] );

  const verifyPhoneFromProfilePage = () => {
    const state = {
      redirectionRoute: '/profile',
    };
    navigate( '/verify-phone', { state } );
  };

  const getUserPrevPhoneNum = async () => {
    const userDataReq = await userApi.getUserData();
    if ( userDataReq.status !== 200 ) return;
    setPrevPhoneNumber( userDataReq.data.phone_number );
  };

  const getUserProfileData = async () => {
    const userDataReq = await userApi.getUserData();
    if ( userDataReq.status !== 200 ) return;

    setFirstName( userDataReq.data.first_name );
    setLastName( userDataReq.data.last_name ?? '' );
    setUserEmail( userDataReq.data.email );
    setUserPlan( userDataReq.data.plan );
    setPhoneNumber( userDataReq.data.phone_number );
    setPhoneVerified( userDataReq.data.is_phone_verified );

    setEmailSubscribed( userDataReq.data.is_email_subscribed );

    //Check last name and phone number was previously filled and keep track of it
    setIsLastNamePrevFilled( userDataReq.data.last_name ? true : false );
    setIsPhoneNumPrevFilled( userDataReq.data.phone_number ? true : false );
  };

  const handleEditSaveToggle = async isSaveMode => {
    setIsEditing( prevIsEditing => !prevIsEditing );
    if ( !isSaveMode ) return;
    if ( firstName === undefined || firstName === '' || !/\S/.test( firstName ) ) {
      setAlertMessage( {
        message: 'Please enter a valid first name.',
        severity: 'error',
      } );
      setValidationConflict( true );
      return;
    }

    if ( isLastNamePrevFilled && ( lastName === undefined || lastName === '' || !/\S/.test( lastName ) ) ) {
      setAlertMessage( {
        message: 'Please enter a valid last name.',
        severity: 'error',
      } );
      setValidationConflict( true );
      setIsEditing( false );
      return;
    }

    if ( !isPhoneNumPrevFilled && phoneNumber !== '' && !isPhoneNumberValid ) {
      setAlertMessage( {
        message: 'Please enter a valid phone number.',
        severity: 'error',
      } );
      setValidationConflict( true );
      setIsEditing( false );
      return;
    }

    if ( isPhoneNumPrevFilled && !isPhoneNumberValid ) {
      setAlertMessage( {
        message: 'Please enter a valid phone number.',
        severity: 'error',
      } );
      setValidationConflict( true );
      setIsEditing( false );
      return;
    }

    setAlertMessage( '' );
    setValidationConflict( false );

    try {
      const updateUserDataReq = await userApi.updateUserData( firstName, lastName, phoneNumber, emailSubscribed );

      //Update profile data
      getUserProfileData();
      getUserDataIntoMiscContext( true );
      setValidationConflict( true );
      setAlertMessage( {
        message: 'Your personal details were successfully saved.',
        severity: 'success',
      } );
      setIsEditing( true );

      if ( prevPhoneNumber !== phoneNumber ) {
        await delay( 3 );
        verifyPhoneFromProfilePage();
      }
    } catch ( error ) {
      if ( error.response.status === 409 ) {
        setValidationConflict( true );
        setAlertMessage( {
          message: error?.response?.data?.detail ?? 'Something went wrong. Please try again.',
          severity: 'error',
        } );
        setIsEditing( false );
        return;
      }

      if ( error.response ) {
        setValidationConflict( true );
        setAlertMessage( {
          message: 'Something went wrong. Please try again.',
          severity: 'error',
        } );
        setIsEditing( false );
        return;
      }
    }
  };

  const handleBack = () => {
    navigate( -1 );
  };

  return (
    <ProfileBase handleBack={ handleBack } backButtonName={ 'Back' }>
      <Stack alignItems="left">
        <Stack direction="row" alignItems="center">
          <Typography
            variant="h4"
            sx={ {
              fontSize: isGalaxyFold ? '21px' : isGalaxyS8Plus ? '28px' : isSmallScreen ? '30px' : '40px',
            } }
          >
            Profile
          </Typography>
          { isEditing ? (
            <Button
              onClick={ () => handleEditSaveToggle( false ) }
              startIcon={ <FontAwesomeIcon icon={ faPen } size="xs" style={ { color: '#36C6CB', fontSize: 16 } } /> }
              sx={ {
                ml: 2,
              } }
            >
              <Typography sx={ textStyles } textAlign="right">
                Edit
              </Typography>
            </Button>
          ) : (
            <Button
              onClick={ () => handleEditSaveToggle( true ) }
              startIcon={ <FontAwesomeIcon icon={ faFloppyDisk } size="xs" style={ { color: '#36C6CB', fontSize: 16 } } /> }
              sx={ {
                ml: 2,
              } }
            >
              <Typography sx={ textStyles } textAlign="right">
                Save
              </Typography>
            </Button>
          ) }
        </Stack>
        <Typography
          color="gray"
          sx={ {
            mb: 4,
            fontSize: isGalaxyFold ? '11px' : isSmallScreen ? '14px' : '16px',
          } }
        >
          You can update your personal information below.
        </Typography>
        { validationConflict ? (
          <Alert
            severity={ alertMessage.severity }
            sx={ {
              width: '100%',
              boxSizing: 'border-box',
              marginY: '10px !important',
            } }
          >
            { alertMessage.message }
          </Alert>
        ) : (
          ''
        ) }
        <Grid container maxWidth={ 750 } spacing={ 1 } rowGap={ 2 }>
          <Grid item xs={ 12 } md={ 6 } sx={ { mb: 1 } }>
            <FormControl fullWidth>
              <TextField
                label="First Name"
                disabled={ isEditing }
                value={ firstName }
                onChange={ e => {
                  setFirstName( e.target.value );
                } }
                variant="outlined"
                fullWidth
              />
            </FormControl>
          </Grid>
          <Grid item xs={ 12 } md={ 6 } sx={ { mb: 1 } }>
            <FormControl fullWidth>
              <TextField
                label="Last Name"
                disabled={ isEditing }
                value={ lastName }
                onChange={ e => {
                  setLastName( e.target.value );
                } }
                variant="outlined"
                fullWidth
              />
            </FormControl>
          </Grid>
          <Grid item xs={ 12 } md={ 6 } sx={ { my: 1 } }>
            <FormControl fullWidth>
              <AutoFillAwareTextField
                autoComplete="new-email"
                label="Email Address"
                disabled={ true }
                hasValue={ true }
                value={ userEmail }
                variant="outlined"
                fullWidth
              />
            </FormControl>
          </Grid>
          <Grid item xs={ 12 } md={ 6 } sx={ { my: 1 } }>
            <FormControl fullWidth>
              <TelInput
                value={ phoneNumber }
                disabled={ isEditing }
                onChange={ ( value, isValid ) => {
                  setPhoneNumber( value );
                  setIsPhoneNumberValid( isValid );
                } }
                key={ !isPhoneNumPrevFilled ? 'empty' : 'non-empty' }
              />
              <Box
                sx={ {
                  display: phoneVerified || !isPhoneNumPrevFilled ? 'none' : 'flex',
                  justifyContent: 'space-between',
                } }
              >
                <Tooltip title="Click here to verify your phone number">
                  <Button startIcon={ <ErrorOutlineIcon /> } color="error" onClick={ verifyPhoneFromProfilePage }>
                    Not verified
                  </Button>
                </Tooltip>
              </Box>
            </FormControl>
          </Grid>
          <Grid item xs={ 12 } md={ 12 }>
            <Typography
              variant="subtitle1"
              sx={ {
                fontSize: isGalaxyS8Plus ? '14px' : isSmallScreen ? '15px' : '16px',
                mr: 2,
                fontWeight: 500,
              } }
            >
              Plan: { userPlan.charAt( 0 ).toUpperCase() + userPlan.slice( 1 ) }
            </Typography>
          </Grid>
          <Grid item xs={ 12 } md={ 12 }>
            <Paper elevation={ 3 } sx={ { padding: 2, backgroundColor: '#f5f5f5', boxShadow: 'revert' } }>
              <Typography variant="h6" sx={ { mb: 1 } }>
                Available Credits
              </Typography>
              <Typography variant="h4" color="black" sx={ { fontWeight: 'bold' } }>
              <FontAwesomeIcon icon={faCoinVertical} size="1x" style={{color: 'gold', marginRight: '10px'}}  />
                {userCredits ? userCredits : 0}
              </Typography>
            </Paper>
          </Grid>
          <Grid item xs={ 12 }>
            <FormControlLabel
              labelPlacement="end"
              disabled={ isEditing }
              control={
                <Checkbox
                  checked={ emailSubscribed }
                  onChange={ e => {
                    setEmailSubscribed( e.target.checked );
                  } }
                />
              }
              label="Subscribed to newsletter."
            />
          </Grid>
        </Grid>
      </Stack>
    </ProfileBase>
  );
};
